/**
 * OnboardingFolderService
 *
 * Manages OnboardingFolder entities for organizing onboardings into folders.
 *
 * Structure:
 * - projects/{projectId}/onboardingFolders/{folderId}
 *
 * Sprint 1.2 - OnboardingFolderService
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  FirestoreDataConverter,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import {
  OnboardingFolder,
  onboardingFolderConverter,
} from '../converters/onboardingFolderConverter';

/**
 * Input type for creating a new folder
 */
export interface CreateFolderInput {
  name: string;
  parentFolderId?: string | null;
}

/**
 * Input type for updating a folder
 */
export interface UpdateFolderInput {
  name?: string;
}

/**
 * OnboardingFolderService
 *
 * Provides CRUD operations for onboarding folders
 */
export class OnboardingFolderService {
  private readonly collectionName = 'onboardingFolders';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<OnboardingFolder>;

  constructor(converter: FirestoreDataConverter<OnboardingFolder> = onboardingFolderConverter) {
    this.converter = converter;
  }

  /**
   * Create a new folder
   *
   * @param projectId - Project ID
   * @param data - Folder data (name, optional parentFolderId)
   * @returns Created folder with generated id
   */
  async create(projectId: string, data: CreateFolderInput): Promise<OnboardingFolder> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = doc(collectionRef).withConverter(this.converter);

      const now = new Date().toISOString();
      const folderData: OnboardingFolder = {
        id: docRef.id,
        projectId,
        name: data.name,
        parentFolderId: data.parentFolderId ?? null,
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(docRef, folderData);
      return folderData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OnboardingFolderService.create');
      throw error;
    }
  }

  /**
   * Get a folder by ID
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID
   * @returns Folder or null if not found
   */
  async get(projectId: string, folderId: string): Promise<OnboardingFolder | null> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      ).withConverter(this.converter);

      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { ...docSnap.data(), id: docSnap.id };
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OnboardingFolderService.get');
      throw error;
    }
  }

  /**
   * List all folders for a project
   *
   * @param projectId - Project ID
   * @returns Array of folders ordered by name
   */
  async list(projectId: string): Promise<OnboardingFolder[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      ).withConverter(this.converter);

      const q = query(collectionRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OnboardingFolderService.list');
      throw error;
    }
  }

  /**
   * Update a folder
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID
   * @param updates - Fields to update
   */
  async update(projectId: string, folderId: string, updates: UpdateFolderInput): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      ).withConverter(this.converter);

      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(docRef, updateData as OnboardingFolder, { merge: true });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OnboardingFolderService.update');
      throw error;
    }
  }

  /**
   * Delete a folder
   *
   * NOTE: This does not cascade delete onboardings in the folder.
   * The UI should handle moving or warning about nested onboardings.
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID to delete
   */
  async delete(projectId: string, folderId: string): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      );

      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OnboardingFolderService.delete');
      throw error;
    }
  }
}
